import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Cpu,
  ChevronRight,
  Filter,
  RefreshCw,
  Eye,
  Play,
} from "lucide-react";
import { predictiveAPI } from "../api";
import clsx from "clsx";
import toast from "react-hot-toast";

const riskConfig = {
  critical: {
    label: "Critical",
    color: "badge-danger",
    bgColor: "bg-red-50 border-red-200",
  },
  high: {
    label: "High",
    color: "badge-warning",
    bgColor: "bg-yellow-50 border-yellow-200",
  },
  medium: {
    label: "Medium",
    color: "badge-primary",
    bgColor: "bg-blue-50 border-blue-200",
  },
  low: {
    label: "Low",
    color: "badge-neutral",
    bgColor: "bg-slate-50 border-slate-200",
  },
};

const InsightCard = ({ insight, onAcknowledge, onAction }) => {
  const risk = riskConfig[insight.risk_level] || riskConfig.low;
  const predictedDate = insight.predicted_date
    ? new Date(insight.predicted_date)
    : null;
  const daysUntil = predictedDate
    ? Math.ceil((predictedDate - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={clsx("card p-5 border", risk.bgColor)}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">
              {insight.component}
            </h3>
            <p className="text-sm text-slate-500">{insight.machine_name}</p>
          </div>
        </div>
        <span className={clsx("badge", risk.color)}>{risk.label} Risk</span>
      </div>

      <p className="text-slate-600 mb-4">{insight.description}</p>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-400 mb-1">Confidence</p>
          <p className="text-lg font-bold text-slate-800">
            {Math.round((insight.confidence_score || 0) * 100)}%
          </p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-400 mb-1">Predicted</p>
          <p className="text-lg font-bold text-slate-800">
            {daysUntil !== null
              ? daysUntil > 0
                ? `${daysUntil} days`
                : "Imminent"
              : "N/A"}
          </p>
        </div>
      </div>

      {insight.recommended_actions &&
        insight.recommended_actions.length > 0 && (
          <div className="mb-4">
            <p className="text-xs text-slate-500 mb-2">Recommended Actions:</p>
            <ul className="space-y-1">
              {insight.recommended_actions.slice(0, 3).map((action, i) => (
                <li
                  key={i}
                  className="text-sm text-slate-500 flex items-center gap-2"
                >
                  <ChevronRight className="w-3 h-3 text-blue-500" />
                  {typeof action === "string"
                    ? action
                    : action.actionTaken || "Action"}
                </li>
              ))}
            </ul>
          </div>
        )}

      <div className="flex items-center gap-2 pt-4 border-t border-slate-200">
        {!insight.is_acknowledged && (
          <button
            onClick={() => onAcknowledge(insight.id)}
            className="btn btn-secondary btn-sm flex-1"
          >
            <Eye className="w-3 h-3" />
            Acknowledge
          </button>
        )}
        {!insight.is_actioned && (
          <button
            onClick={() => onAction(insight.id)}
            className="btn btn-primary btn-sm flex-1"
          >
            <Play className="w-3 h-3" />
            Take Action
          </button>
        )}
        {insight.is_actioned && (
          <span className="badge badge-success flex-1 justify-center">
            <CheckCircle className="w-3 h-3 mr-1" />
            Actioned
          </span>
        )}
      </div>
    </motion.div>
  );
};

const PredictiveInsights = () => {
  const [riskFilter, setRiskFilter] = useState("");
  const queryClient = useQueryClient();

  const { data: insightsData, isLoading } = useQuery({
    queryKey: ["predictive-insights", { riskLevel: riskFilter }],
    queryFn: () =>
      predictiveAPI
        .getActiveInsights({
          riskLevel: riskFilter || undefined,
        })
        .then((res) => res.data),
  });

  const { data: sparePredictions } = useQuery({
    queryKey: ["spare-predictions"],
    queryFn: () =>
      predictiveAPI.getSparePredictions().then((res) => res.data.data),
  });

  const acknowledgeMutation = useMutation({
    mutationFn: (id) => predictiveAPI.acknowledge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["predictive-insights"] });
      toast.success("Insight acknowledged");
    },
  });

  const actionMutation = useMutation({
    mutationFn: (id) =>
      predictiveAPI.action(id, {
        actionTaken: "Reviewed and scheduled for maintenance",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["predictive-insights"] });
      toast.success("Action recorded");
    },
  });

  const insights = insightsData?.data || [];

  const stats = {
    critical: insights.filter((i) => i.risk_level === "critical").length,
    high: insights.filter((i) => i.risk_level === "high").length,
    medium: insights.filter((i) => i.risk_level === "medium").length,
    total: insights.length,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Predictive Insights
          </h1>
          <p className="text-slate-500">
            AI-powered failure predictions and recommendations
          </p>
        </div>
        <button className="btn btn-secondary">
          <RefreshCw className="w-4 h-4" />
          Run Analysis
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="stat-card border-red-200 bg-red-50"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">Critical</p>
              <p className="text-2xl font-bold text-red-500">
                {stats.critical}
              </p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-500 opacity-50" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="stat-card border-yellow-200 bg-yellow-50"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">High Risk</p>
              <p className="text-2xl font-bold text-yellow-500">{stats.high}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-yellow-500 opacity-50" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="stat-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">Medium Risk</p>
              <p className="text-2xl font-bold text-blue-500">{stats.medium}</p>
            </div>
            <Clock className="w-8 h-8 text-blue-500 opacity-50" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="stat-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">Total Active</p>
              <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
            </div>
            <Cpu className="w-8 h-8 text-slate-400 opacity-50" />
          </div>
        </motion.div>
      </div>

      <div className="flex items-center gap-4">
        <select
          value={riskFilter}
          onChange={(e) => setRiskFilter(e.target.value)}
          className="input w-auto"
        >
          <option value="">All Risk Levels</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl skeleton" />
                <div className="flex-1">
                  <div className="h-5 w-32 skeleton mb-2" />
                  <div className="h-4 w-24 skeleton" />
                </div>
              </div>
              <div className="h-16 skeleton mb-4" />
              <div className="grid grid-cols-2 gap-4">
                <div className="h-16 skeleton rounded-lg" />
                <div className="h-16 skeleton rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      ) : insights.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {insights.map((insight) => (
            <InsightCard
              key={insight.id}
              insight={insight}
              onAcknowledge={(id) => acknowledgeMutation.mutate(id)}
              onAction={(id) => actionMutation.mutate(id)}
            />
          ))}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-800 mb-2">
            All Clear!
          </h3>
          <p className="text-slate-500">
            No active predictive insights at this time.
          </p>
        </div>
      )}

      {sparePredictions && sparePredictions.length > 0 && (
        <div className="card p-6">
          <h3 className="font-semibold text-slate-800 mb-4">
            Spare Parts Alert
          </h3>
          <div className="space-y-3">
            {sparePredictions.slice(0, 5).map((part) => (
              <div
                key={part.id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
              >
                <div>
                  <p className="font-medium text-slate-800">{part.name}</p>
                  <p className="text-sm text-slate-500">
                    Part #: {part.part_number}
                  </p>
                </div>
                <div className="text-right">
                  <p
                    className={clsx(
                      "font-bold",
                      part.quantity_in_stock <= 0
                        ? "text-red-500"
                        : "text-yellow-500"
                    )}
                  >
                    {part.quantity_in_stock} in stock
                  </p>
                  <p className="text-xs text-slate-400">
                    Reorder at {part.reorder_level}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PredictiveInsights;
